import React, { useState, useEffect, useCallback } from 'react';
import { Check, Truck, Package, ChevronDown, ChevronUp, CalendarClock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function HitachiPlanGrid({ plans, projectId, project, planDate, onRefresh, onDelete, isAdmin = true }) {
  const [seatTypes, setSeatTypes] = useState([]);
  const [setStatuses, setSetStatuses] = useState({});
  const [dailyProd, setDailyProd] = useState({});
  const [dailyPlan, setDailyPlan] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [expandedSet, setExpandedSet] = useState(null);
  const [editingLabel, setEditingLabel] = useState(null);
  const [labelValue, setLabelValue] = useState('');
  const [reschedulingSet, setReschedulingSet] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');

  // Separate active plans from rescheduled ones
  const activePlans = plans
    .filter(p => p.is_hitachi && p.set_number && p.status !== 'rescheduled')
    .sort((a, b) => (a.set_number || 0) - (b.set_number || 0));

  const rescheduledPlans = plans
    .filter(p => p.is_hitachi && p.set_number && p.status === 'rescheduled')
    .sort((a, b) => (a.set_number || 0) - (b.set_number || 0));

  const hitachiPlans = activePlans;
  const hitachiSetNums = hitachiPlans.map(p => p.set_number).join(',');
  const rescheduledSetNums = rescheduledPlans.map(p => p.set_number).join(',');

  useEffect(() => {
    if (!projectId) return;
    axios.get(`${API}/api/hitachi/config/${projectId}`)
      .then(res => setSeatTypes(res.data.seat_types || []))
      .catch(() => {});
  }, [projectId]);

  const fetchSetData = useCallback(async () => {
    if ((!hitachiPlans.length && !rescheduledPlans.length) || !seatTypes.length) return;
    const statuses = {};
    const daily = {};
    const planned = {};
    // Fetch active plans
    await Promise.all(hitachiPlans.map(async (plan) => {
      try {
        const [statusRes, prodRes] = await Promise.all([
          axios.get(`${API}/api/hitachi/sets/${projectId}/${plan.set_number}`),
          axios.get(`${API}/api/hitachi/production/${projectId}/${plan.set_number}/${planDate}`),
        ]);
        statuses[plan.set_number] = statusRes.data;
        daily[plan.set_number] = prodRes.data.production || {};
        planned[plan.set_number] = prodRes.data.planned || {};
      } catch { /* ignore */ }
    }));
    // Fetch status for rescheduled plans (no daily data needed)
    await Promise.all(rescheduledPlans.map(async (plan) => {
      if (statuses[plan.set_number]) return; // Already fetched
      try {
        const res = await axios.get(`${API}/api/hitachi/sets/${projectId}/${plan.set_number}`);
        statuses[plan.set_number] = res.data;
      } catch { /* ignore */ }
    }));
    setSetStatuses(statuses);
    setDailyProd(daily);
    setDailyPlan(planned);
  }, [hitachiSetNums, rescheduledSetNums, seatTypes.length, projectId, planDate]);

  useEffect(() => { fetchSetData(); }, [fetchSetData]);

  const handleSave = async (planId, setNumber) => {
    const currentProd = dailyProd[setNumber] || {};
    const currentPlan = dailyPlan[setNumber] || {};
    try {
      await axios.put(`${API}/api/hitachi/production/${planId}`, {
        planned: currentPlan,
        production: currentProd,
        notes: '',
      });
      toast.success(`Set #${setNumber} saved`);
      fetchSetData();
      if (onRefresh) onRefresh();
    } catch { toast.error('Failed to save'); }
  };

  const handleCellEdit = (setNumber, field, materialCode, currentVal) => {
    setEditingCell(`${setNumber}-${field}-${materialCode}`);
    setEditValue(String(currentVal || 0));
  };

  const handleCellSave = (setNumber, field, materialCode) => {
    const val = Math.max(0, parseInt(editValue) || 0);
    if (field === 'pln') {
      setDailyPlan(prev => ({
        ...prev,
        [setNumber]: { ...(prev[setNumber] || {}), [materialCode]: val },
      }));
    } else {
      setDailyProd(prev => ({
        ...prev,
        [setNumber]: { ...(prev[setNumber] || {}), [materialCode]: val },
      }));
    }
    setEditingCell(null);
  };

  const handleShip = async (planId, setNumber) => {
    try {
      await axios.put(`${API}/api/hitachi/plans/${planId}/ship`);
      toast.success(`Set #${setNumber} shipped`);
      fetchSetData();
      if (onRefresh) onRefresh();
    } catch { toast.error('Failed to ship'); }
  };

  const handleUnship = async (planId, setNumber) => {
    try {
      await axios.put(`${API}/api/hitachi/plans/${planId}/unship`);
      toast.success(`Set #${setNumber} unshipped`);
      fetchSetData();
      if (onRefresh) onRefresh();
    } catch { toast.error('Failed'); }
  };

  const saveSetLabel = async (planId) => {
    try {
      await axios.put(`${API}/api/plans/${planId}`, { set_numbers: labelValue });
      if (onRefresh) onRefresh();
    } catch { toast.error('Failed to save set number'); }
    setEditingLabel(null);
  };

  const handleReschedule = async (planId, setNum) => {
    if (!rescheduleDate) { toast.error('Please select a date'); return; }
    try {
      await axios.post(`${API}/api/hitachi/plans/${planId}/reschedule`, { new_date: rescheduleDate });
      toast.success(`Set #${setNum} rescheduled to ${rescheduleDate}`);
      setReschedulingSet(null);
      setRescheduleDate('');
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reschedule');
    }
  };

  if (!hitachiPlans.length && !rescheduledPlans.length) return (
    <div className="px-4 py-3 text-center text-[11px] text-slate-400 italic">
      No Hitachi sets planned for this day. Use the + button to add sets.
    </div>
  );

  const totalSets = hitachiPlans.length;
  const completeSets = hitachiPlans.filter(p => {
    const st = setStatuses[p.set_number];
    return st && (st.status === 'completed' || st.status === 'shipped');
  }).length;
  const shippedSets = hitachiPlans.filter(p => p.shipped_quantity > 0).length;

  const renderEditableCell = (setNum, field, materialCode, currentVal, colorClass) => {
    const cellKey = `${setNum}-${field}-${materialCode}`;
    const isEditing = editingCell === cellKey;
    const isShipped = hitachiPlans.find(p => p.set_number === setNum)?.shipped_quantity > 0;

    if (isAdmin && !isShipped) {
      if (isEditing) {
        return (
          <input type="number" min="0" value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={() => handleCellSave(setNum, field, materialCode)}
            onKeyDown={e => { if (e.key === 'Enter') handleCellSave(setNum, field, materialCode); if (e.key === 'Escape') setEditingCell(null); }}
            className={`w-14 border rounded px-1 py-0.5 text-center text-xs font-bold focus:outline-none focus:ring-1 ${
              field === 'pln' ? 'border-blue-400 text-blue-700 focus:ring-blue-400' : 'border-emerald-400 text-emerald-700 focus:ring-emerald-400'
            }`}
            autoFocus data-testid={`${field}-input-${setNum}-${materialCode}`} />
        );
      }
      return (
        <span className={`inline-block w-14 text-center font-bold cursor-pointer hover:bg-slate-50 rounded py-0.5 ${currentVal > 0 ? colorClass : 'text-slate-300'}`}
          onClick={() => handleCellEdit(setNum, field, materialCode, currentVal)}
          data-testid={`${field}-cell-${setNum}-${materialCode}`}>
          {currentVal}
        </span>
      );
    }
    return <span className={`font-bold ${currentVal > 0 ? colorClass : 'text-slate-300'}`}>{currentVal}</span>;
  };

  return (
    <div className="divide-y divide-slate-100" data-testid="hitachi-plan-grid">
      {/* Summary header */}
      <div className="px-4 py-2 bg-indigo-50/50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="w-4 h-4 text-indigo-500" />
          <span className="text-xs font-bold text-indigo-700">Hitachi Rail Sets</span>
          <span className="text-[10px] text-indigo-400">{totalSets} set{totalSets !== 1 ? 's' : ''} planned</span>
        </div>
        <div className="flex items-center gap-2 text-[10px]">
          {completeSets > 0 && <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">{completeSets} complete</span>}
          {shippedSets > 0 && <span className="bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-semibold">{shippedSets} shipped</span>}
        </div>
      </div>

      {/* Sets */}
      {hitachiPlans.map(plan => {
        const setNum = plan.set_number;
        const status = setStatuses[setNum] || {};
        const todayProd = dailyProd[setNum] || {};
        const todayPlan = dailyPlan[setNum] || {};
        const isExpanded = expandedSet === setNum;
        const isComplete = status.status === 'completed' || status.status === 'shipped';
        const isShipped = plan.shipped_quantity > 0;

        return (
          <div key={plan.id} className={`${isShipped ? 'bg-violet-50/30' : isComplete ? 'bg-emerald-50/30' : ''}`}
            data-testid={`hitachi-set-${setNum}`}>
            {/* Set Header Row */}
            <div className="px-4 py-2 flex items-center justify-between cursor-pointer hover:bg-slate-50"
              onClick={() => setExpandedSet(isExpanded ? null : setNum)}>
              <div className="flex items-center gap-2">
                {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                <span className="text-sm font-bold text-slate-800">Set #{setNum}</span>
                {editingLabel === plan.id ? (
                  <input value={labelValue} onChange={e => setLabelValue(e.target.value)}
                    onBlur={() => saveSetLabel(plan.id)}
                    onKeyDown={e => { if (e.key === 'Enter') saveSetLabel(plan.id); if (e.key === 'Escape') setEditingLabel(null); }}
                    onClick={e => e.stopPropagation()}
                    className="border border-indigo-300 rounded px-1.5 py-0.5 text-xs w-24 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    autoFocus data-testid={`set-label-input-${setNum}`} />
                ) : (
                  <span className="text-[10px] text-indigo-500 font-medium cursor-pointer hover:bg-indigo-50 px-1 rounded"
                    onClick={e => { e.stopPropagation(); if (isAdmin) { setEditingLabel(plan.id); setLabelValue(plan.set_numbers || `#${setNum}`); } }}
                    data-testid={`set-label-${setNum}`}>
                    {plan.set_numbers || `#${setNum}`}
                  </span>
                )}
                <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${
                  isShipped ? 'bg-violet-100 text-violet-700' :
                  isComplete ? 'bg-emerald-100 text-emerald-700' :
                  status.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                  'bg-slate-100 text-slate-400'
                }`} data-testid={`set-status-badge-${setNum}`}>
                  {isShipped ? 'Shipped' : isComplete ? 'Complete' : status.status === 'in_progress' ? 'In Progress' : 'Planned'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-500">{status.total_produced || 0}/{status.total_required || 0}</span>
                <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${isShipped ? 'bg-violet-500' : isComplete ? 'bg-emerald-500' : 'bg-amber-400'}`}
                    style={{ width: `${status.total_required ? Math.min(100, Math.round((status.total_produced || 0) / status.total_required * 100)) : 0}%` }} />
                </div>
                {isAdmin && isComplete && !isShipped && (
                  <button onClick={e => { e.stopPropagation(); handleShip(plan.id, setNum); }}
                    className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold bg-violet-500 text-white rounded hover:bg-violet-600"
                    data-testid={`ship-btn-${setNum}`}>
                    <Truck className="w-3 h-3" /> Ship
                  </button>
                )}
                {isAdmin && isShipped && (
                  <button onClick={e => { e.stopPropagation(); handleUnship(plan.id, setNum); }}
                    className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold bg-slate-200 text-slate-600 rounded hover:bg-slate-300"
                    data-testid={`unship-btn-${setNum}`}>
                    Undo Ship
                  </button>
                )}
                {isAdmin && !isShipped && (
                  <button onClick={e => { e.stopPropagation(); setReschedulingSet(reschedulingSet === setNum ? null : setNum); setRescheduleDate(''); }}
                    className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold bg-amber-100 text-amber-700 rounded hover:bg-amber-200"
                    data-testid={`reschedule-btn-${setNum}`}>
                    <CalendarClock className="w-3 h-3" /> Reschedule
                  </button>
                )}
              </div>
            </div>

            {/* Reschedule date picker */}
            {reschedulingSet === setNum && (
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-3" onClick={e => e.stopPropagation()}
                data-testid={`reschedule-panel-${setNum}`}>
                <CalendarClock className="w-4 h-4 text-amber-600" />
                <span className="text-xs font-semibold text-amber-700">Reschedule Set #{setNum} to:</span>
                <input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)}
                  className="border border-amber-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400"
                  data-testid={`reschedule-date-${setNum}`} />
                <button onClick={() => handleReschedule(plan.id, setNum)}
                  className="px-3 py-1 text-[10px] font-bold bg-amber-600 text-white rounded hover:bg-amber-700"
                  data-testid={`reschedule-confirm-${setNum}`}>
                  Confirm
                </button>
                <button onClick={() => { setReschedulingSet(null); setRescheduleDate(''); }}
                  className="px-2 py-1 text-[10px] font-semibold text-slate-500 hover:text-slate-700"
                  data-testid={`reschedule-cancel-${setNum}`}>
                  Cancel
                </button>
              </div>
            )}

            {/* Expanded Detail — Single table with PLN + PRD columns */}
            {isExpanded && (
              <div className="px-4 pb-3">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-[9px] text-slate-400 uppercase tracking-wider border-b border-slate-200">
                      <th className="py-1.5 text-left font-semibold">Seat Type</th>
                      <th className="py-1.5 text-center font-semibold w-14">Req</th>
                      <th className="py-1.5 text-center font-semibold w-16 text-blue-500">PLN</th>
                      <th className="py-1.5 text-center font-semibold w-16 text-emerald-500">PRD</th>
                      <th className="py-1.5 text-center font-semibold w-16">Cumul.</th>
                      <th className="py-1.5 text-center font-semibold w-16 text-orange-500">Remain</th>
                      <th className="py-1.5 text-center font-semibold w-10">Done</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seatTypes.map(st => {
                      const detail = (status.details || []).find(d => d.material_code === st.material_code) || {};
                      const planQty = todayPlan[st.material_code] || 0;
                      const prodQty = todayProd[st.material_code] || 0;
                      const cumulative = detail.produced || 0;
                      const remaining = detail.remaining !== undefined ? detail.remaining : st.qty_per_set;
                      const isDone = detail.complete;

                      return (
                        <tr key={st.material_code} className={`border-b border-slate-100 ${isDone ? 'bg-emerald-50/30' : ''}`}>
                          <td className="py-1.5 font-medium text-slate-700">
                            <div className="flex items-center gap-1.5">
                              {st.type === 'other' && <span className="text-[8px] bg-slate-200 text-slate-500 px-1 rounded">OTH</span>}
                              <div>
                                <span className="truncate max-w-[200px] block">{st.name}</span>
                                <span className="text-[9px] text-slate-400">{st.material_code}</span>
                              </div>
                            </div>
                          </td>
                          <td className="py-1.5 text-center font-bold text-slate-600">{st.qty_per_set}</td>
                          <td className="py-1.5 text-center">
                            {renderEditableCell(setNum, 'pln', st.material_code, planQty, 'text-blue-700 bg-blue-50')}
                          </td>
                          <td className="py-1.5 text-center">
                            {renderEditableCell(setNum, 'prd', st.material_code, prodQty, 'text-emerald-700 bg-emerald-50')}
                          </td>
                          <td className="py-1.5 text-center font-bold text-slate-600">{cumulative}</td>
                          <td className="py-1.5 text-center">
                            <span className={`font-bold ${remaining > 0 ? 'text-orange-600' : 'text-slate-300'}`}>{remaining}</span>
                          </td>
                          <td className="py-1.5 text-center">
                            {isDone ? <Check className="w-4 h-4 text-emerald-500 mx-auto" /> : <span className="text-slate-200">-</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold text-xs">
                      <td className="py-1.5 text-slate-600">TOTAL</td>
                      <td className="py-1.5 text-center text-slate-600">{seatTypes.reduce((s, st) => s + st.qty_per_set, 0)}</td>
                      <td className="py-1.5 text-center text-blue-600">{seatTypes.reduce((s, st) => s + (todayPlan[st.material_code] || 0), 0)}</td>
                      <td className="py-1.5 text-center text-emerald-600">{seatTypes.reduce((s, st) => s + (todayProd[st.material_code] || 0), 0)}</td>
                      <td className="py-1.5 text-center text-slate-600">{status.total_produced || 0}</td>
                      <td className="py-1.5 text-center text-orange-600">{(status.total_required || 0) - (status.total_produced || 0)}</td>
                      <td className="py-1.5 text-center">
                        {isComplete ? <Check className="w-4 h-4 text-emerald-500 mx-auto" /> : ''}
                      </td>
                    </tr>
                  </tfoot>
                </table>
                {isAdmin && !isShipped && (
                  <div className="mt-2 flex justify-end">
                    <button onClick={() => handleSave(plan.id, setNum)}
                      className="px-4 py-1.5 text-[10px] font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                      data-testid={`save-btn-${setNum}`}>
                      Save
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Rescheduled sets info */}
      {rescheduledPlans.length > 0 && (
        <div className="px-4 py-2 bg-slate-50/50 border-t border-slate-200">
          <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mb-1">Rescheduled from this day</div>
          {rescheduledPlans.map(plan => (
            <div key={plan.id} className="flex items-center gap-2 py-1 text-[11px]" data-testid={`rescheduled-info-${plan.set_number}`}>
              <CalendarClock className="w-3.5 h-3.5 text-slate-400" />
              <span className="font-semibold text-slate-500">Set #{plan.set_number}</span>
              <span className="text-slate-400">moved to</span>
              <span className="font-semibold text-amber-600">{plan.rescheduled_to || '?'}</span>
              {(() => {
                const st = setStatuses[plan.set_number];
                if (!st) return null;
                return (
                  <span className="text-slate-400">
                    ({st.total_produced || 0}/{st.total_required || 0} produced)
                  </span>
                );
              })()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
